// ExpiryReport.js — Inventory ▸ Expiry Report.
// Batch-wise expiry of store stock, read from /stockreport/expiry-report.
// Fully dynamic — no static values. Deep-linkable via ?bucket=expired|today|
// next7|next30 so the notification bell / dashboard widget open it pre-filtered.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Container, Card, Typography, Grid, Chip, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Button, CircularProgress, Snackbar, Alert, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const currency = company?.currency ?? '₹';

// Status → visual style (spec colors).
const STATUS = {
  Expired: { label: 'Expired', color: '#DC2626', bg: '#FEE2E2' },
  Today: { label: 'Expires Today', color: '#DC2626', bg: '#FEE2E2' },
  Next7: { label: 'Expiring ≤ 7 Days', color: '#EA580C', bg: '#FFEDD5' },
  Next30: { label: 'Expiring ≤ 30 Days', color: '#CA8A04', bg: '#FEF9C3' },
  Safe: { label: 'Safe', color: '#16A34A', bg: '#DCFCE7' },
  NoExpiry: { label: 'No Expiry', color: '#64748B', bg: '#F1F5F9' },
};

const BUCKETS = [
  { key: 'all', label: 'All' },
  { key: 'expired', label: 'Expired' },
  { key: 'today', label: 'Today' },
  { key: 'next7', label: 'Next 7 Days' },
  { key: 'next30', label: 'Next 30 Days' },
];

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const daysLabel = (d) =>
  d == null ? '—' : d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : `${d} days`;

const inBucket = (r, bucket) => {
  const d = r.daysRemaining;
  if (d == null) return bucket === 'all';
  switch (bucket) {
    case 'expired': return d < 0;
    case 'today': return d === 0;
    case 'next7': return d >= 1 && d <= 7;
    case 'next30': return d >= 8 && d <= 30;
    default: return true;
  }
};

export default function ExpiryReport() {
  const token = sessionStorage.getItem('token');
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const [bucket, setBucket] = useState(params.get('bucket') || 'all');
  const [search, setSearch] = useState('');
  const [supplier, setSupplier] = useState('');
  const [store, setStore] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/stockreport/expiry-report/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setRows(json.rows || []);
    } catch {
      setSnack({ open: true, msg: 'Failed to load expiry report', sev: 'error' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { setBucket(params.get('bucket') || 'all'); }, [params]);

  const suppliers = useMemo(() => [...new Set(rows.map((r) => r.supplier).filter(Boolean))], [rows]);
  const stores = useMemo(() => [...new Set(rows.map((r) => r.store).filter(Boolean))], [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (!inBucket(r, bucket)) return false;
    if (supplier && r.supplier !== supplier) return false;
    if (store && r.store !== store) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(r.itemName || '').toLowerCase().includes(q) && !(r.batchNumber || '').toLowerCase().includes(q)) return false;
    }
    if (from && (!r.expiryDate || new Date(r.expiryDate) < new Date(from))) return false;
    if (to && (!r.expiryDate || new Date(r.expiryDate) > new Date(to))) return false;
    return true;
  }), [rows, bucket, supplier, store, search, from, to]);

  const counts = useMemo(() => ({
    all: rows.length,
    expired: rows.filter((r) => r.daysRemaining != null && r.daysRemaining < 0).length,
    today: rows.filter((r) => r.daysRemaining === 0).length,
    next7: rows.filter((r) => r.daysRemaining >= 1 && r.daysRemaining <= 7).length,
    next30: rows.filter((r) => r.daysRemaining >= 8 && r.daysRemaining <= 30).length,
  }), [rows]);

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filtered.map((r, i) => ({
      '#': i + 1,
      Item: r.itemName,
      Batch: r.batchNumber || '',
      Supplier: r.supplier || '',
      'Purchase Date': fmt(r.purchaseDate),
      'Expiry Date': fmt(r.expiryDate),
      'Days Remaining': daysLabel(r.daysRemaining),
      'Qty Available': r.qtyAvailable,
      Unit: r.unit || '',
      Store: r.store || '',
      Status: STATUS[r.status]?.label || r.status,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Expiry');
    XLSX.writeFile(wb, `Expiry_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      {/* Banner */}
      <Card
        className='no-print'
        elevation={0}
        sx={{ p: 2.5, mb: 2, borderRadius: 3, color: '#fff',
          background: 'linear-gradient(135deg,#0b1120 0%,#0f172a 40%,#7f1d1d 100%)',
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <span className='material-symbols-rounded' style={{ fontSize: 30 }}>event_busy</span>
        <Box>
          <Typography variant='h5' fontWeight={800} sx={{ color: '#fff' }}>Inventory Expiry Report</Typography>
          <Typography sx={{ color: '#cbd5e1', fontSize: 13 }}>Batch-wise expiry tracking across all stores · FEFO ready</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant='outlined' onClick={load} startIcon={<RefreshIcon />} sx={{ color: '#fff', borderColor: '#ffffff55' }}>Refresh</Button>
        <Button variant='outlined' onClick={() => window.print()} startIcon={<PrintIcon />} sx={{ color: '#fff', borderColor: '#ffffff55' }}>PDF</Button>
        <Button variant='outlined' onClick={exportExcel} startIcon={<DownloadIcon />} sx={{ color: '#fff', borderColor: '#ffffff55' }}>Excel</Button>
      </Card>

      {/* Bucket chips */}
      <Box className='no-print' sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {BUCKETS.map((b) => (
          <Chip key={b.key} label={`${b.label} (${counts[b.key] ?? 0})`}
            onClick={() => setBucket(b.key)}
            color={bucket === b.key ? 'primary' : 'default'}
            variant={bucket === b.key ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }} />
        ))}
      </Box>

      {/* Filters */}
      <Card className='no-print' elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, border: '1px solid #E7ECF3' }}>
        <Grid container spacing={1.2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size='small' placeholder='Search item / batch' value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position='start'><SearchIcon fontSize='small' /></InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField select fullWidth size='small' value={supplier} onChange={(e) => setSupplier(e.target.value)} SelectProps={{ displayEmpty: true }}>
              <MenuItem value=''>All Suppliers</MenuItem>
              {suppliers.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField select fullWidth size='small' value={store} onChange={(e) => setStore(e.target.value)} SelectProps={{ displayEmpty: true }}>
              <MenuItem value=''>All Stores</MenuItem>
              {stores.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size='small' type='date' label='Expiry From' InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size='small' type='date' label='Expiry To' InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E7ECF3', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: '#475569', bgcolor: '#F8FAFC' } }}>
                  <TableCell>Item</TableCell><TableCell>Batch</TableCell><TableCell>Supplier</TableCell>
                  <TableCell>Purchase Date</TableCell><TableCell>Expiry Date</TableCell><TableCell>Days Remaining</TableCell>
                  <TableCell align='right'>Qty Available</TableCell><TableCell>Store</TableCell><TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={9} align='center' sx={{ py: 4, color: '#64748b' }}>No matching batches.</TableCell></TableRow>
                )}
                {filtered.map((r) => {
                  const s = STATUS[r.status] || STATUS.Safe;
                  return (
                    <TableRow key={r.batchId} hover sx={r.status === 'Expired' ? { bgcolor: '#FEF2F2' } : undefined}>
                      <TableCell sx={{ fontWeight: 600 }}>{r.itemName}</TableCell>
                      <TableCell>{r.batchNumber || '—'}</TableCell>
                      <TableCell>{r.supplier || '—'}</TableCell>
                      <TableCell>{fmt(r.purchaseDate)}</TableCell>
                      <TableCell>{fmt(r.expiryDate)}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: s.color }}>{daysLabel(r.daysRemaining)}</TableCell>
                      <TableCell align='right'>{r.qtyAvailable} {r.unit || ''}</TableCell>
                      <TableCell>{r.store || '—'}</TableCell>
                      <TableCell><Chip size='small' label={s.label} sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700 }} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
